import React from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import TrainerLayout from '../../components/trainer/TrainerLayout';
import {
  BookOpen,
  Users,
  GraduationCap,
  TrendingUp,
  Star,
  Clock,
  ChevronRight,
} from 'lucide-react';

const stats = [
  { label: 'My Courses', value: 3, icon: BookOpen, color: 'bg-amber-50 text-amber-500' },
  { label: 'Total Students', value: 456, icon: Users, color: 'bg-green-50 text-green-600' },
  { label: 'Active Enrollments', value: 312, icon: GraduationCap, color: 'bg-purple-50 text-purple-600' },
  { label: 'Avg. Rating', value: '4.8', icon: Star, color: 'bg-amber-50 text-amber-600' },
];

const recentStudents = [
  { name: 'Rahul Mehta', course: 'AI for Finance', progress: 65, lastActive: '2 min ago' },
  { name: 'Ananya Krishnan', course: 'AI for Finance', progress: 90, lastActive: '1 hour ago' },
  { name: 'Vikram Joshi', course: 'Data Science for Banking', progress: 30, lastActive: '3 hours ago' },
  { name: 'Priya Nair', course: 'AI for Finance', progress: 10, lastActive: '1 day ago' },
];

const upcomingSessions = [
  { title: 'Week 7: FP&A Forecasting', course: 'AI for Finance', date: 'Sat, May 24', time: '10:00 AM IST' },
  { title: 'Week 3: Python Basics', course: 'Data Science for Banking', date: 'Wed, May 28', time: '7:00 PM IST' },
];

export default function TrainerDashboard(): React.JSX.Element {
  return (
    <TrainerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Trainer Dashboard</h1>
          <p className="text-sm text-slate-500">Welcome back! Here's how your students are doing.</p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl p-5 border border-slate-100"
              >
                <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center mb-3`}>
                  <Icon className="w-5 h-5" />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-xs text-slate-500 mt-1">{stat.label}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Students */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
          >
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Recent Students</h3>
              <Link to="/trainer/students" className="text-sm text-amber-500 font-semibold hover:underline">View All</Link>
            </div>
            <div className="divide-y divide-slate-100">
              {recentStudents.map((s, i) => (
                <div key={i} className="p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-sm">
                    {s.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{s.name}</p>
                    <p className="text-xs text-slate-500">{s.course}</p>
                  </div>
                  <div className="text-right">
                    <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden mb-1">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: `${s.progress}%` }} />
                    </div>
                    <p className="text-xs text-slate-500">{s.progress}%</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Upcoming Sessions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
          >
            <div className="p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-900">Upcoming Sessions</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {upcomingSessions.map((session, i) => (
                <div key={i} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{session.title}</p>
                      <p className="text-xs text-slate-500">{session.course}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-xs font-bold">Upcoming</span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {session.date}
                    </span>
                    <span>{session.time}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-slate-100">
              <button className="w-full py-2.5 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 transition-colors">
                + Schedule New Session
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </TrainerLayout>
  );
}
